import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import AddIcon from '@material-ui/icons/Add';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { RebacExplorerClient } from '../../api/RebacExplorerClient';
import type { Grant, GrantRelation, GrantsResponse } from '../../api/RebacExplorerClient';
import { GrantsGrid } from './GrantsGrid';
import { GrantForm } from './GrantForm';
import { RevokeModal } from './RevokeModal';

interface EntityPermissionsTabProps {
  resourceType: string;
  resourceId: string;
  onDeleteResource?: () => void;
}

type Toast = { message: string; severity: 'success' | 'error' };

export function EntityPermissionsTab({ resourceType, resourceId, onDeleteResource }: EntityPermissionsTabProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const client = new RebacExplorerClient({ discoveryApi, fetchApi });

  const objectLabel = `${resourceType}:${resourceId}`;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GrantsResponse | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [availableRelations, setAvailableRelations] = useState<GrantRelation[]>(['viewer', 'editor', 'admin']);
  const [showForm, setShowForm] = useState(false);
  const [crossTenantError, setCrossTenantError] = useState<string | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<Grant | null>(null);

  const [toast, setToast] = useState<Toast | null>(null);

  const loadGrants = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const result = await client.getGrants(resourceType, resourceId);
      setData(result);
    } catch (err: any) {
      setFetchError(err.message ?? 'Falha ao carregar grants');
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadGrants(); }, [loadGrants]);

  useEffect(() => {
    client.getRelationsForType(resourceType)
      .then(setAvailableRelations)
      .catch(() => {});
  }, [resourceType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGrant = async (target: string, relation: GrantRelation, grantDuration?: string) => {
    setCrossTenantError(null);
    try {
      await client.grantAccess({ target, relation, object: objectLabel, grantDuration });
      setShowForm(false);
      setToast({ message: `Acesso ${relation} concedido a ${target.split('/').pop()}`, severity: 'success' });
      await loadGrants();
    } catch (err: any) {
      const msg: string = err.message ?? '';
      if (msg.toLowerCase().includes('cross-tenant') || msg.toLowerCase().includes('tenant')) {
        setCrossTenantError(msg);
      } else {
        setToast({ message: `Erro ao conceder acesso: ${msg}`, severity: 'error' });
      }
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await client.revokeAccess(resourceType, resourceId, {
        target: revokeTarget.user,
        relation: revokeTarget.relation,
      });
      setRevokeTarget(null);
      setToast({
        message: `Acesso ${revokeTarget.relation} de ${revokeTarget.user} revogado`,
        severity: 'success',
      });
      await loadGrants();
    } catch (err: any) {
      setRevokeTarget(null);
      setToast({ message: `Erro ao revogar: ${err.message}`, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box style={{ padding: 16 }}>
        <Alert severity="error">{fetchError}</Alert>
      </Box>
    );
  }

  const canManage = data?.can_manage ?? false;
  const canDelete = data?.can_delete ?? false;
  const grants = data?.grants ?? [];

  return (
    <Box style={{ padding: '16px 0' }}>
      {!canManage && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            borderRadius: 6,
            backgroundColor: '#fff8e1',
            border: '1px solid #ffe082',
            marginBottom: 16,
          }}
        >
          <LockOutlinedIcon fontSize="small" style={{ color: '#f57f17' }} />
          <Typography variant="body2" style={{ color: '#7c5100' }}>
            Acesso somente leitura — você não tem permissão para gerenciar este objeto.
          </Typography>
        </Box>
      )}

      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Permissões — {objectLabel}
        </Typography>
        {canManage && !showForm && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setShowForm(true); setCrossTenantError(null); }}
          >
            Conceder Acesso
          </Button>
        )}
      </Box>

      {showForm && (
        <GrantForm
          objectLabel={objectLabel}
          availableRelations={availableRelations}
          onSubmit={handleGrant}
          onCancel={() => { setShowForm(false); setCrossTenantError(null); }}
          crossTenantError={crossTenantError}
        />
      )}

      <GrantsGrid
        grants={grants}
        canManage={canManage}
        canDelete={canDelete}
        onRevoke={setRevokeTarget}
        onDelete={onDeleteResource}
      />

      {revokeTarget && (
        <RevokeModal
          grant={revokeTarget}
          objectLabel={objectLabel}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}

      {toast && (
        <Snackbar
          open
          autoHideDuration={5000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
